/**
 * Helpers for building upstream API URLs from untrusted values (query params,
 * cookies, profile data). Interpolating a raw value into a URL *path* is a
 * traversal/SSRF vector: `encodeURIComponent` on the segment turns `/`, `..`
 * separators and `?`/`#` into their percent forms so a value can never break
 * out of its intended segment.
 */

/**
 * Encode a single path segment so it cannot introduce extra path separators,
 * traversal, or a query/fragment. Use for every variable segment spliced into
 * an API path (`.../teams/${encodePathSegment(id)}/detail`).
 */
export const encodePathSegment = (segment: string | number): string =>
  encodeURIComponent(String(segment))

/** MongoDB ObjectId shape — 24 hex chars. */
const OBJECT_ID = /^[a-f\d]{24}$/i

/** True if `value` is a 24-hex-char ObjectId. Reject upstream ids that aren't. */
export const isObjectId = (value: unknown): value is string =>
  typeof value === 'string' && OBJECT_ID.test(value)

// Cinematics tags are short slugs (alnum, `_`, `-`). Anything else — dots,
// slashes, encoded traversal — is not a tag and must be refused, not encoded.
const CINEMATICS_TAG = /^[A-Za-z0-9_-]{1,64}$/

/** True if `value` is a well-formed cinematics tag slug. */
export const isCinematicsTag = (value: unknown): value is string =>
  typeof value === 'string' && CINEMATICS_TAG.test(value)

// `\` is an authority separator for special schemes and C0 controls (incl.
// TAB/CR/LF, \x00-\x1f, and DEL \x7f) are stripped by WHATWG URL parsing
// before the origin is decided, so `/\evil.com` and `/%09/evil.com` resolve
// off-origin while passing a naive `startsWith('/')` check. Reject them
// outright before parsing.
export const UNSAFE_PATH_CHARS = /[\\\x00-\x1f\x7f]/

// Every canonical API path is plain ASCII: segments of alnum, `.`, `_`, `-`
// separated by `/`. Anything outside this set never needs to reach upstream.
const CANONICAL_PATH_CHARS = /^\/[A-Za-z0-9/._-]*$/

export type PathnameRejectReason =
  | 'not_a_path'          // non-string or missing leading '/'
  | 'unsafe_chars'        // backslash / C0 controls / DEL
  | 'malformed_encoding'  // decodeURIComponent threw (bad `%`, overlong UTF-8)
  | 'encoded_form'        // decoded !== raw — any percent-encoding, incl. double-encoding
  | 'bad_charset'         // chars outside [A-Za-z0-9/._-]
  | 'empty_segment'       // `//` anywhere
  | 'trailing_slash'      // `/path/`
  | 'dot_segment'         // `.` or `..` segment

export type NormalizedPathname =
  | { ok: true; path: string }
  | { ok: false; reason: PathnameRejectReason }

/**
 * Prove a pathname is already canonical — never rewrite it.
 *
 * Consumers of the result (the catch-all whitelist, rate limiting, the
 * basic-auth endpoint check) compare it against tables of canonical paths and
 * then let the ORIGINAL string flow onward (`create-handler` proxies
 * `req.nextUrl.pathname` verbatim). Rewriting here would mean validating one
 * string and proxying another, so instead: decode exactly once and require
 * identity with the input. Anything that doesn't normalize clean — encoded
 * separators, doubled slashes, dot segments, trailing slash, control chars —
 * is rejected, not repaired. On success `path` is always `===` the input.
 */
export const normalizePathname = (raw: unknown): NormalizedPathname => {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return { ok: false, reason: 'not_a_path' }
  if (UNSAFE_PATH_CHARS.test(raw)) return { ok: false, reason: 'unsafe_chars' }

  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return { ok: false, reason: 'malformed_encoding' }
  }
  if (decoded !== raw) return { ok: false, reason: 'encoded_form' }
  if (!CANONICAL_PATH_CHARS.test(raw)) return { ok: false, reason: 'bad_charset' }

  const segments = raw.slice(1).split('/')
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    if (segment === '') {
      return { ok: false, reason: i === segments.length - 1 ? 'trailing_slash' : 'empty_segment' }
    }
    if (segment === '.' || segment === '..') return { ok: false, reason: 'dot_segment' }
  }

  return { ok: true, path: raw }
}
